import moment from 'moment';
import { PlanItemsRepository } from '@/libs/tpm-analytics/data/plan-items.repository';
import { ScrapRecordsRepository } from '@/libs/tpm-analytics/data/scrap-records.repository';
import {
    calcPlanItemDowntimeCostKc,
    calcPlanItemLostProfitKc,
    calcPlanItemMaintenanceSplitKc,
    calcScrapCostKc,
    getHourlyDowntimeCosts,
    getHourlyDowntimeLostProfit,
    getHourlyMaintenanceCosts,
    buildWorkplaceRatesCache,
    setWorkplaceRatesCache,
} from '@/libs/tpm-analytics/core/financial.calculator';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { WorkplaceModel, LocationModel } from '@/libs/Api';
import {
    computeUnionCostsByWorkplace,
    computeUnionCostsTotal,
    computeUnionCostsDailyTotal,
    computeUnionCostsMonthlyTotal,
    computeUnionTwoCostsDailyMonthlyTotal,
    computeUnionMinutesTotal,
} from '@/libs/tpm-analytics/core/downtime.utils';
import { productionDayKey } from '@/libs/tpm-analytics/core/production-day.utils';
import {
    FinancialCostsByWorkplaceItem,
    FinancialCostsTrendDay,
    FinancialImpactSummary,
    FinancialImpactTrendPoint,
    FinancialImpactTrendPointDay,
    FinancialImpactTrendByWorkplace,
} from '@/libs/tpm-analytics/core/types';

/**
 * Načte pracoviště a lokace ze stores a správně je propojí pro hierarchickou resoluce nákladů.
 * 
 * @returns Pole workplace objektů s napojenými location a parentLocation objekty
 */
function getWorkplacesWithLocations(): any[] {
    // Načteme všechna pracoviště a lokace ze stores
    const workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) || [];
    const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) || [];
    
    // Vytvoříme mapu lokací pro rychlý přístup
    const locationsMap = new Map<string, any>();
    for (const loc of locations) {
        if (loc.id) {
            locationsMap.set(loc.id, { ...loc });
        }
    }
    
    // Propojíme lokace s jejich parent locations
    for (const loc of locationsMap.values()) {
        if (loc.locationParentId) {
            loc.parentLocation = locationsMap.get(loc.locationParentId);
        }
    }
    
    // Propojíme pracoviště s lokacemi
    const result = workplaces.map((wp) => {
        const workplace = { ...wp };
        if (workplace.locationId) {
            workplace.location = locationsMap.get(workplace.locationId);
        }
        return workplace;
    });
    
    return result;
}

/**
 * Shrnutí finančního dopadu (prostoje + zmetky + ušlý zisk) za období.
 * Pozn.: výpočet běží čistě na klientovi nad GraphQL daty (žádná REST agregace).
 */
export async function getFinancialImpactSummary(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactSummary> {
    // Načtení odstávek a záznamů zmetků paralelně pro rychlost
    // Pozn.: GraphQL vrací hrubá čísla (minuty, hodinové sazby, počty kusů) – převod na Kč provádíme zde.
    //        Chybějící hodnoty (null/undefined) bereme jako 0, abychom nemíchali NaN do výsledků.
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    // Načteme pracoviště a lokace ze stores a předpočítáme hodinové sazby (výrazné zvýšení výkonu)
    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);

    try {
        // Pro prostoje a ušlý zisk použijeme jeden průchod nad oběma sazbami
        // Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
        // Nyní s cache pro optimalizaci výkonu
        const bundle = computeUnionTwoCostsDailyMonthlyTotal(
            planItems as any[],
            (p) => getHourlyDowntimeCosts(p),
            (p) => getHourlyDowntimeLostProfit(p),
            fromIso,
            toIso
        );
        const downtimeCostsKc = bundle.totalA;
        const lostProfitKc = bundle.totalB;
        const scrapCostsKc = shiftScrapRecs.reduce((sum, r: any) => sum + calcScrapCostKc(r), 0);
        const unionDowntimeMinutes = computeUnionMinutesTotal(planItems as any[], fromIso, toIso);

        // Náklady na údržbu - per-item (ne union, protože jiná sazba než downtime)
        let maintenanceCostsKc = 0;
        let repairCostsKc = 0;
        for (const p of planItems as any[]) {
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            repairCostsKc += repairKc;
            maintenanceCostsKc += maintenanceKc;
        }

        // Zjistíme kompletnost dat pro náklady na zmetky
        const totalScrapRecords = shiftScrapRecs.length;
        const scrapRecordsWithCost = shiftScrapRecs.filter((r: any) => {
            const unitCost = Number(r?.scrapCostsPerUnit ?? r?.product?.scrapCosts ?? 0) || 0;
            return unitCost > 0;
        }).length;
        const scrapCompletenessPercent = totalScrapRecords > 0 
            ? Math.round((scrapRecordsWithCost / totalScrapRecords) * 100) 
            : 100;

        const totalImpactKc = Math.round(downtimeCostsKc + maintenanceCostsKc + repairCostsKc + lostProfitKc + scrapCostsKc); // celkový dopad (zaokrouhlený)

        // Vypočítáme stejné období „o krok zpět" pro meziobdobní porovnání
        const from = moment(fromIso);
        const to = moment(toIso);
        // Pozor na okrajové případy: minimálně 1 minuta, aby „předchozí období" nebylo nulové
        const span = Math.max(1, to.diff(from, 'minutes'));
        const prevTo = from.clone();
        const prevFrom = from.clone().add(-span, 'minutes');
        const prev = await getFinancialImpactSummaryNoPrev(prevFrom.toISOString(), prevTo.toISOString(), filters);

        return { 
            totalImpactKc, 
            downtimeCostsKc,
            maintenanceCostsKc: Math.round(maintenanceCostsKc),
            repairCostsKc: Math.round(repairCostsKc),
            scrapCostsKc, 
            lostProfitKc, 
            prev, 
            meta: { 
                unionDowntimeMinutes,
                scrapDataCompleteness: {
                    totalRecords: totalScrapRecords,
                    recordsWithCost: scrapRecordsWithCost,
                    completenessPercent: scrapCompletenessPercent,
                },
            } 
        };
    } finally {
        // Vyčistíme cache po dokončení výpočtu
        setWorkplaceRatesCache(null);
    }
}

async function getFinancialImpactSummaryNoPrev(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<{ totalImpactKc: number; downtimeCostsKc: number; maintenanceCostsKc: number; repairCostsKc: number; scrapCostsKc: number; lostProfitKc: number }> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);
    
    // Načteme pracoviště a lokace ze stores a předpočítáme hodinové sazby (výrazné zvýšení výkonu)
    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);
    
    try {
        // Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
        // Nyní s cache pro optimalizaci výkonu
        const bundle = computeUnionTwoCostsDailyMonthlyTotal(
            planItems as any[],
            (p) => getHourlyDowntimeCosts(p),
            (p) => getHourlyDowntimeLostProfit(p),
            fromIso,
            toIso
        );
        const downtimeCostsKc = bundle.totalA;
        const lostProfitKc = bundle.totalB;
        const scrapCostsKc = shiftScrapRecs.reduce((sum, r: any) => sum + calcScrapCostKc(r), 0);
        const unionDowntimeMinutes = computeUnionMinutesTotal(planItems as any[], fromIso, toIso);
        
        // Náklady na údržbu
        let maintenanceCostsKc = 0;
        let repairCostsKc = 0;
        for (const p of planItems as any[]) {
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            repairCostsKc += repairKc;
            maintenanceCostsKc += maintenanceKc;
        }
        
        const totalImpactKc = Math.round(downtimeCostsKc + maintenanceCostsKc + repairCostsKc + lostProfitKc + scrapCostsKc);
        return { 
            totalImpactKc, 
            downtimeCostsKc, 
            maintenanceCostsKc: Math.round(maintenanceCostsKc), 
            repairCostsKc: Math.round(repairCostsKc), 
            scrapCostsKc, 
            lostProfitKc 
        };
    } finally {
        // Vyčistíme cache po dokončení výpočtu
        setWorkplaceRatesCache(null);
    }
}

/**
 * Měsíční trend finančního dopadu – agregujeme do klíče YYYY-MM-01.
 */
export async function getFinancialImpactTrendMonthly(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactTrendPoint[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    // Načteme pracoviště a lokace ze stores a předpočítáme hodinové sazby (výrazné zvýšení výkonu)
    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);

    try {
        const map = new Map<string, { downtime: number; maintenance: number; scrap: number; lost: number }>(); // klíč = první den měsíce
        const put = (mKey: string, d: number, m: number, s: number, l: number) => {
            const e = map.get(mKey) || { downtime: 0, maintenance: 0, scrap: 0, lost: 0 };
            e.downtime += d;
            e.maintenance += m;
            e.scrap += s;
            e.lost += l;
            map.set(mKey, e);
        };

        // Jediný průchod pro obě sazby (downtime + lost profit) a měsíce
        // Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
        // Nyní s cache pro optimalizaci výkonu
        const monthlyBundle = computeUnionTwoCostsDailyMonthlyTotal(
            planItems as any[],
            (p) => getHourlyDowntimeCosts(p),
            (p) => getHourlyDowntimeLostProfit(p),
            fromIso,
            toIso
        );
        for (const [mKey, v] of monthlyBundle.monthlyA.entries()) put(mKey, v, 0, 0, 0);
        for (const [mKey, v] of monthlyBundle.monthlyB.entries()) put(mKey, 0, 0, 0, v);
        // Maintenance costs per-item
        for (const p of planItems as any[]) {
            const dateStr = p.shutdownFromDate || p.shutdownToDate || fromIso;
            const dayKey = productionDayKey(Date.parse(dateStr));
            const mKey = moment(dayKey).startOf('month').format('YYYY-MM-01');
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            put(mKey, 0, repairKc + maintenanceKc, 0, 0);
        }
        // Zmetky - nejprve přiřadíme k výrobnímu dni, pak určíme měsíc
        for (const r of shiftScrapRecs as any[]) {
            const dateStr = r.recordTime || fromIso;
            const dayKey = productionDayKey(Date.parse(dateStr));
            const mKey = moment(dayKey).startOf('month').format('YYYY-MM-01');
            put(mKey, 0, 0, calcScrapCostKc(r), 0);
        }
        // řazení časové osy (vzestupně)
        const months = Array.from(map.keys()).sort();
        return months.map((k) => ({
            month: k,
            downtimeKc: Math.round(map.get(k)!.downtime),
            maintenanceKc: Math.round(map.get(k)!.maintenance),
            scrapKc: Math.round(map.get(k)!.scrap),
            lostProfitKc: Math.round(map.get(k)!.lost),
        }));
    } finally {
        // Vyčistíme cache po dokončení výpočtu
        setWorkplaceRatesCache(null);
    }
}

export async function getFinancialImpactTrendDaily(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactTrendPointDay[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    // Načteme pracoviště a lokace ze stores a předpočítáme hodinové sazby (výrazné zvýšení výkonu)
    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);

    try {
        const map = new Map<string, { downtime: number; maintenance: number; scrap: number; lost: number }>(); // klíč = YYYY-MM-DD
        const put = (dKey: string, d: number, m: number, s: number, l: number) => {
            const e = map.get(dKey) || { downtime: 0, maintenance: 0, scrap: 0, lost: 0 };
            e.downtime += d;
            e.maintenance += m;
            e.scrap += s;
            e.lost += l;
            map.set(dKey, e);
        };
        // Jeden průchod pro downtime + lost profit a rovnou denní mapy (bez druhého průchodu)
        // Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
        // Nyní s cache pro optimalizaci výkonu
        const bundle = computeUnionTwoCostsDailyMonthlyTotal(
            planItems as any[],
            (p) => getHourlyDowntimeCosts(p),
            (p) => getHourlyDowntimeLostProfit(p),
            fromIso,
            toIso
        );
        for (const [key, v] of bundle.dailyA.entries()) put(key, v, 0, 0, 0);
        for (const [key, v] of bundle.dailyB.entries()) put(key, 0, 0, 0, v);
        // Maintenance costs per-item
        for (const p of planItems as any[]) {
            const dateStr = p.shutdownFromDate || p.shutdownToDate || fromIso;
            const key = productionDayKey(Date.parse(dateStr));
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            put(key, 0, repairKc + maintenanceKc, 0, 0);
        }
        // Zmetky s použitím výrobního dne (productionDayKey)
        for (const r of shiftScrapRecs as any[]) {
            const dateStr = r.recordTime || fromIso;
            const key = productionDayKey(Date.parse(dateStr));
            put(key, 0, 0, calcScrapCostKc(r), 0);
        }
        // přehled po dnech ve správném pořadí
        const days = Array.from(map.keys()).sort();
        return days.map((k) => ({
            day: k,
            downtimeKc: Math.round(map.get(k)!.downtime),
            maintenanceKc: Math.round(map.get(k)!.maintenance),
            scrapKc: Math.round(map.get(k)!.scrap),
            lostProfitKc: Math.round(map.get(k)!.lost),
        }));
    } finally {
        // Vyčistíme cache po dokončení výpočtu
        setWorkplaceRatesCache(null);
    }
}

/**
 * Denní trend nákladů – rozpad na opravy/údržbu/prostoje/zmetky.
 * Pozn.: Opravy ~ reakční zásah, Údržba ~ zbytek odstávky dle hodinové sazby údržby.
 */
export async function getFinancialCostsTrendDaily(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialCostsTrendDay[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    // Načteme pracoviště a lokace ze stores a předpočítáme hodinové sazby (výrazné zvýšení výkonu)
    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);

    try {
        const map = new Map<string, { repair: number; downtime: number; maintenance: number; scrap: number }>(); // složené kategorie
        const put = (dKey: string, rep: number, down: number, maint: number, scrap: number) => {
            const e = map.get(dKey) || { repair: 0, downtime: 0, maintenance: 0, scrap: 0 };
            e.repair += rep;
            e.downtime += down;
            e.maintenance += maint;
            e.scrap += scrap;
            map.set(dKey, e);
        };
        // DowntimeKc po dnech přes union náklady (opravy/údržba zůstávají per-item)
        // Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
        // Nyní s cache pro optimalizaci výkonu
        const dayDown = computeUnionCostsDailyTotal(
            planItems as any[],
            (p) => getHourlyDowntimeCosts(p),
            fromIso,
            toIso
        );
        for (const [key, v] of dayDown.entries()) put(key, 0, v, 0, 0);
        // Per-item rozpad oprav/údržby s použitím výrobního dne (productionDayKey)
        for (const p of planItems as any[]) {
            const dateStr = p.shutdownFromDate || p.shutdownToDate || fromIso;
            const key = productionDayKey(Date.parse(dateStr));
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            put(key, repairKc, 0, maintenanceKc, 0);
        }
        // Zmetky s použitím výrobního dne (productionDayKey)
        for (const r of shiftScrapRecs as any[]) {
            const dateStr = r.recordTime || fromIso;
            const key = productionDayKey(Date.parse(dateStr));
            put(key, 0, 0, 0, calcScrapCostKc(r));
        }
        // seřadíme dny (x-ová osa grafu)
        const days = Array.from(map.keys()).sort();
        return days.map((k) => ({
            day: k,
            repairKc: Math.round(map.get(k)!.repair),
            downtimeKc: Math.round(map.get(k)!.downtime),
            maintenanceKc: Math.round(map.get(k)!.maintenance),
            scrapKc: Math.round(map.get(k)!.scrap),
        }));
    } finally {
        // Vyčistíme cache po dokončení výpočtu
        setWorkplaceRatesCache(null);
    }
}

export async function getFinancialCostsByWorkplace(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialCostsByWorkplaceItem[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    // Načteme pracoviště a lokace ze stores a předpočítáme hodinové sazby (výrazné zvýšení výkonu)
    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);

    try {
        const map = new Map<string | null, FinancialCostsByWorkplaceItem>();
        // Helper: lazy inicializace položky pro pracoviště (podporuje i neznámé/NULL id)
        const ensure = (wid: string | null, wname: string) => {
            if (!map.has(wid))
                map.set(wid, {
                    workplaceId: wid,
                    workplaceName: wname || '—',
                    downtimeKc: 0,
                    scrapKc: 0,
                    repairKc: 0,
                    maintenanceKc: 0,
                });
            return map.get(wid)!;
        };
        // DowntimeKc přes union náklady po pracovištích
        // Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
        // Nyní s cache pro optimalizaci výkonu
        const unionDownByWp = computeUnionCostsByWorkplace(
            planItems as any[],
            (p) => getHourlyDowntimeCosts(p),
            fromIso,
            toIso
        );
        for (const [wid, downKc] of unionDownByWp.entries()) {
            // Najdeme jméno pracoviště z libovolné položky s daným wid
            const pSample = (planItems as any[]).find((x) => (x?.workplaceId ?? x?.workplace?.id ?? null) === wid);
            const wname: string = pSample?.workplace?.name ?? '—';
            const e = ensure(wid, wname);
            e.downtimeKc += downKc;
        }
        // Opravy/údržba per-item beze změny
        for (const p of planItems as any[]) {
            const wid: string | null = p?.workplaceId ?? p?.workplace?.id ?? null;
            const wname: string = p?.workplace?.name ?? '—';
            const e = ensure(wid, wname);
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            e.repairKc += repairKc;
            e.maintenanceKc += maintenanceKc;
        }
        for (const r of shiftScrapRecs as any[]) {
            const wid: string | null = r?.station?.workplace?.id ?? null;
            const wname: string = r?.station?.workplace?.name ?? '—';
            const e = ensure(wid, wname);
            e.scrapKc += calcScrapCostKc(r);
        }
        // Seřadíme pracoviště podle celkových nákladů (sestupně)
        return Array.from(map.values()).sort(
            (a, b) =>
                b.downtimeKc + b.scrapKc + b.repairKc + b.maintenanceKc - (a.downtimeKc + a.scrapKc + a.repairKc + a.maintenanceKc)
        );
    } finally {
        // Vyčistíme cache po dokončení výpočtu
        setWorkplaceRatesCache(null);
    }
}

/**
 * Finanční dopad podle pracovišť v čase (pro trend graf)
 */
export async function getFinancialCostsTrendByWorkplace(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactTrendByWorkplace[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    const workplaces = getWorkplacesWithLocations();
    const cache = buildWorkplaceRatesCache(workplaces);
    setWorkplaceRatesCache(cache);

    try {
        // workplaceId -> day -> { downtimeKc, maintenanceKc, scrapKc, lostProfitKc }
        const wpMap = new Map<string, { name: string; dayMap: Map<string, { downtimeKc: number; maintenanceKc: number; scrapKc: number; lostProfitKc: number; repairKc: number }> }>();

        const getKey = (dateIso: string): string => {
            const m = moment(dateIso);
            if (granularity === 'week') {
                return m.clone().startOf('isoWeek').format('YYYY-MM-DD');
            } else if (granularity === 'month') {
                return m.clone().startOf('month').format('YYYY-MM-DD');
            } else {
                return productionDayKey(new Date(dateIso).getTime());
            }
        };

        // Process plan items
        for (const p of planItems as any[]) {
            const wid: string | null = p?.workplaceId ?? p?.workplace?.id ?? null;
            if (!wid) continue;
            const wname: string = p?.workplace?.name ?? '—';
            const key = getKey(p.planFrom || p.createdAt);

            if (!wpMap.has(wid)) {
                wpMap.set(wid, { name: wname, dayMap: new Map() });
            }
            const entry = wpMap.get(wid)!;
            const dayData = entry.dayMap.get(key) || { downtimeKc: 0, maintenanceKc: 0, scrapKc: 0, lostProfitKc: 0, repairKc: 0 };

            dayData.downtimeKc += calcPlanItemDowntimeCostKc(p);
            dayData.lostProfitKc += calcPlanItemLostProfitKc(p);
            const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
            dayData.repairKc += repairKc;
            dayData.maintenanceKc += maintenanceKc;

            entry.dayMap.set(key, dayData);
        }

        // Process scrap records
        for (const r of shiftScrapRecs as any[]) {
            const wid: string | null = r?.station?.workplace?.id ?? null;
            if (!wid) continue;
            const wname: string = r?.station?.workplace?.name ?? '—';
            const key = getKey(r.createdAt);

            if (!wpMap.has(wid)) {
                wpMap.set(wid, { name: wname, dayMap: new Map() });
            }
            const entry = wpMap.get(wid)!;
            const dayData = entry.dayMap.get(key) || { downtimeKc: 0, maintenanceKc: 0, scrapKc: 0, lostProfitKc: 0, repairKc: 0 };
            dayData.scrapKc += calcScrapCostKc(r);
            entry.dayMap.set(key, dayData);
        }

        const result = Array.from(wpMap.entries()).map(([workplaceId, data]) => ({
            workplaceId,
            workplaceName: data.name,
            points: Array.from(data.dayMap.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([day, dayData]) => ({
                    day,
                    downtimeKc: dayData.downtimeKc,
                    maintenanceKc: dayData.maintenanceKc + dayData.repairKc, // Spojíme maintenance a repair
                    scrapKc: dayData.scrapKc,
                    lostProfitKc: dayData.lostProfitKc,
                })),
        }));
        
        console.log('[FI] getFinancialCostsTrendByWorkplace result:', {
            wpCount: result.length,
            sampleWp: result[0],
            planItems: planItems.length,
            scrapRecs: shiftScrapRecs.length,
            granularity,
        });
        
        return result;
    } finally {
        setWorkplaceRatesCache(null);
    }
}
